import { useEffect, useState } from 'react';
import type { Testimonial } from '../../data/adventureSocialProof';

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
  /** Título visível acima da secção. */
  title?: string;
  /** Subtítulo opcional. */
  subtitle?: string;
}

const VISIBLE_COUNT = 3;
/** Intervalo entre rotações em milissegundos. */
const ROTATE_INTERVAL_MS = 6000;

/**
 * Secção de testemunhos de jogadores com rotação automática.
 * Mostra até 3 testemunhos de cada vez e vai rodando para novos
 * a cada poucos segundos, estilo galeria. Só renderiza se
 * `testimonials` tiver pelo menos uma entrada.
 */
export default function TestimonialsSection({
  testimonials,
  title = 'O que dizem os jogadores',
  subtitle,
}: TestimonialsSectionProps) {
  if (testimonials.length === 0) return null;

  // Se houver 3 ou menos testemunhos, mostra-os todos fixamente (sem rotação).
  if (testimonials.length <= VISIBLE_COUNT) {
    return (
      <section className="px-4 py-12 md:py-16 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <Header title={title} subtitle={subtitle} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <TestimonialCard key={`${t.authorName}-${i}`} testimonial={t} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return <RotatingGallery testimonials={testimonials} title={title} subtitle={subtitle} />;
}

function Header({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="text-center mb-10">
      <h2 className="text-2xl md:text-3xl font-bold text-dark mb-2">{title}</h2>
      {subtitle && <p className="text-gray-600 max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
}

function RotatingGallery({
  testimonials,
  title,
  subtitle,
}: {
  testimonials: Testimonial[];
  title: string;
  subtitle?: string;
}) {
  const total = testimonials.length;
  const pages = Math.ceil(total / VISIBLE_COUNT);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (pages <= 1) return;
    const timer = setInterval(() => {
      setPage((p) => (p + 1) % pages);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pages]);

  const start = page * VISIBLE_COUNT;
  const visible = testimonials.slice(start, start + VISIBLE_COUNT);

  return (
    <section className="px-4 py-12 md:py-16 bg-white border-t border-gray-100">
      <div className="max-w-6xl mx-auto">
        <Header title={title} subtitle={subtitle} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-opacity duration-500 ease-in-out">
          {visible.map((t, i) => (
            <TestimonialCard key={`${t.authorName}-${start + i}`} testimonial={t} />
          ))}
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            {Array.from({ length: pages }).map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Página ${i + 1} de testemunhos`}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  i === page
                    ? 'bg-primary w-6'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                onClick={() => setPage(i)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <figure className="bg-[#FFFBF5] rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-full">
      <svg
        className="w-8 h-8 text-primary/30 mb-3"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
      </svg>
      <blockquote className="text-gray-700 leading-relaxed flex-1 mb-5">
        “{testimonial.quote}”
      </blockquote>
      <figcaption className="flex items-center gap-3 mt-auto">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent text-white font-bold text-sm flex items-center justify-center shrink-0">
          {testimonial.initials.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-dark text-sm truncate">
            {testimonial.authorName}
          </p>
        </div>
      </figcaption>
    </figure>
  );
}
